import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useIntl } from 'react-intl';
import { useCurrentUser } from '@sdir/auth/lib/hooks';
import { PaymentWindowContainer, PaymentWindowNetsCallback } from '@sdir/payment-window';
import { routes } from '../Routes';
import { checkIsOrganization, ExternalUserRole, PersonRole } from '../../constants/permissions';

const PaymentWindowModule: React.FC<{
  errorCallback: React.Dispatch<React.SetStateAction<boolean>>;
  cancelCallback: () => void;
  successCallback: () => void;
  chargeIds?: number[];
  transactionId?: string;
  responseCode?: string;
}> = ({
  errorCallback,
  cancelCallback,
  successCallback,
  chargeIds = [],
  transactionId = '',
  responseCode = '',
}) => {
  const PaymentWindowStepSession = 'payment_window_step';
  const ApplicationPaymentStepUrlSession = 'application_payment_step_url';
  const intl = useIntl();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const [step, setStep] = useState(window.sessionStorage.getItem(PaymentWindowStepSession) ?? '1');
  const [error, setError] = useState('');
  const [token, setToken] = useState(currentUser?.access_token || '');

  const isExternalUser = useMemo(() => {
    const role = currentUser?.profile?.role;
    const hasExternalRole = role?.includes(ExternalUserRole);
    const hasPersonRole = role?.includes(PersonRole);
    const hasOrgRole = checkIsOrganization(currentUser);
    return hasExternalRole && !hasPersonRole && !hasOrgRole;
  }, [currentUser?.profile?.role]);

  const representationId = useMemo(() => {
    if (isExternalUser) {
      return currentUser?.profile?.externalUserId || '';
    }
    return currentUser?.profile?.reportee || '';
  }, [currentUser?.profile, isExternalUser]);

  useEffect(() => {
    setToken(currentUser?.access_token as string);
  }, [currentUser]);

  useEffect(() => {
    window.sessionStorage.setItem(PaymentWindowStepSession, step);
  }, [step]);

  const beforeRedirect = useCallback(() => {
    setStep('2');
  }, []);

  useEffect(() => {
    if (!responseCode || !transactionId) {
      setStep('1');
    }
  }, [responseCode, transactionId]);

  const goBackToFirstStep = useCallback(() => {
    setStep('1');
  }, []);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    window.sessionStorage.setItem(ApplicationPaymentStepUrlSession, location.pathname);
  }, []);

  const netsRedirectUrl = useMemo(() => {
    return `${window.location.origin + window.location.pathname}#${routes.paymentWindow}`;
  }, [window?.location]);

  const onProcessFailCallback = useCallback(
    (errorMessage: any) => {
      console.log('something failed when processing payment: ', errorMessage);
      setError(intl.formatMessage({ id: 'application.payment.failed' }));
      errorCallback(true);
    },
    [intl, errorCallback]
  );

  return (
    <div>
      {error ? (
        <p>{error}</p>
      ) : (
        <>
          {step === '1' && token && (
            <PaymentWindowContainer
              unpaidChargesIds={chargeIds}
              beforeRedirect={beforeRedirect}
              token={token}
              netsRedirectUrl={netsRedirectUrl}
              goBackToPortal={cancelCallback}
              goBackButtonText={intl.formatMessage({ id: 'general.backtoapplications' })}
              representationId={representationId}
              externalUser={isExternalUser}
              useNewDesign
              language={intl.locale === 'en' ? 2 : 1}
              showDelete={false}
            />
          )}
          {step === '2' && token && (
            <PaymentWindowNetsCallback
              goBackToFirstStep={goBackToFirstStep}
              goBackToPortal={successCallback}
              netsResponseCode={responseCode}
              netsTransactionId={transactionId}
              token={token}
              representationId={representationId}
              unpaidChargesIds={chargeIds}
              externalUser={isExternalUser}
              langauge={intl.locale === 'en' ? 2 : 1}
              goBackButtonText={intl.formatMessage({ id: 'general.backtoapplications' })}
              onProcessFailCallback={onProcessFailCallback}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PaymentWindowModule;
